import { useQuery, type UseQueryOptions } from "@tanstack/react-query";
import { ApiError, get } from "./api";
import { setLogoLang } from "./brandLang";
import type { Branding, Me, OrgDetail, OrgSummary, PropertyDetail, PropertySummary } from "./types";

export const qk = {
  me: ["me"] as const,
  branding: (host: string, org?: string | null) => ["branding", host, org ?? ""] as const,
  orgs: ["orgs"] as const,
  org: (slug: string) => ["org", slug] as const,
  properties: (orgId: string) => ["properties", orgId] as const,
  property: (id: string) => ["property", id] as const,
  members: (orgId: string) => ["members", orgId] as const,
  propertyMembers: (id: string) => ["property-members", id] as const,
  mailboxes: (orgId: string) => ["mailboxes", orgId] as const,
  domains: (orgId: string) => ["domains", orgId] as const,
  emails: (orgId: string, q: string, status: string) => ["emails", orgId, q, status] as const,
  superadmins: ["superadmins"] as const,
};

export function useMe() {
  return useQuery<Me | null>({
    queryKey: qk.me,
    queryFn: async () => {
      try {
        const me = await get<Me>("/api/me");
        setLogoLang(me);
        return me;
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) return null;
        throw e;
      }
    },
    staleTime: 60_000,
  });
}

export function useBranding(orgSlug?: string | null) {
  const host = window.location.hostname;
  return useQuery<Branding & { isWhitelabelHost: boolean }>({
    queryKey: qk.branding(host, orgSlug),
    queryFn: () => get(`/api/public/branding?host=${encodeURIComponent(host)}${orgSlug ? `&org=${encodeURIComponent(orgSlug)}` : ""}`),
    staleTime: 5 * 60_000,
  });
}

export const useOrgs = () => useQuery<OrgSummary[]>({ queryKey: qk.orgs, queryFn: () => get("/api/orgs") });

export const useOrg = (slug: string | undefined, opts?: Partial<UseQueryOptions<OrgDetail>>) =>
  useQuery<OrgDetail>({ queryKey: qk.org(slug ?? ""), queryFn: () => get(`/api/orgs/by-slug/${slug}`), enabled: !!slug, ...opts });

export const useProperties = (orgId: string | undefined) =>
  useQuery<PropertySummary[]>({ queryKey: qk.properties(orgId ?? ""), queryFn: () => get(`/api/orgs/${orgId}/properties`), enabled: !!orgId });

/** Archivált ingatlanok (visszaállításhoz) – csak kérésre töltjük be. */
export const useArchivedProperties = (orgId: string | undefined, enabled: boolean) =>
  useQuery<PropertySummary[]>({ queryKey: [...qk.properties(orgId ?? ""), "archived"], queryFn: () => get(`/api/orgs/${orgId}/properties?archived=true`), enabled: !!orgId && enabled });

export const useProperty = (id: string | undefined) =>
  useQuery<PropertyDetail>({ queryKey: qk.property(id ?? ""), queryFn: () => get(`/api/properties/${id}`), enabled: !!id });
